import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { getStoredLocation, requestLocation } from "@/lib/location";
import { CATEGORIES, AGE_GROUPS, DURATIONS, CONDITIONS, EXCHANGE_MODES, PHONE_PLACEHOLDER, type ExchangeMode } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { lookupIsbn } from "@/lib/isbn-lookup";
import { compressImage, formatBytes } from "@/lib/image-compress";
import { pageHead } from "@/lib/seo";


export const Route = createFileRoute("/add-book")({
  head: () => pageHead({ path: "/add-book", title: "Add a book — Book Swap", description: "List a book you're happy to swap, lend or donate to a neighbour on Book Swap.", noindex: true }),
  component: AddBook,
});

function AddBook() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "", author: "", isbn: "",
    category: "", age_group: "", exchange_duration: "", condition: "",
    description: "", phone: "", library_id: "" as string,
    exchange_mode: "swap" as ExchangeMode,
  });
  const [libraries, setLibraries] = useState<Array<{ id: string; name: string }>>([]);
  const [isbnLookingUp, setIsbnLookingUp] = useState(false);
  const [importedCoverUrl, setImportedCoverUrl] = useState<string | null>(null);

  async function onIsbnLookup() {
    const raw = form.isbn.trim();
    if (!raw) return toast.error("Please enter an ISBN.");
    setIsbnLookingUp(true);
    try {
      const result = await lookupIsbn(raw);
      if (!result.ok) {
        if (result.reason === "invalid") toast.error("Please enter a valid ISBN.");
        else toast.error("We couldn't find this ISBN. Please enter the book details manually.");
        return;
      }
      const d = result.data;
      setForm((f) => ({
        ...f,
        title: f.title || d.title,
        author: f.author || d.author,
        isbn: d.isbn,
        description: f.description || (d.description ?? ""),
        category: f.category || (d.category && (CATEGORIES as readonly string[]).includes(d.category) ? d.category : f.category),
      }));
      if (d.cover_url && !coverFile) {
        setImportedCoverUrl(d.cover_url);
        setCoverPreview(d.cover_url);
      }
      toast.success("Book details imported. Review and edit before listing.");
    } finally {
      setIsbnLookingUp(false);
    }
  }


  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("phone_number").eq("id", user.id).maybeSingle()
      .then(({ data }) => { if (data?.phone_number) setForm((f) => ({ ...f, phone: data.phone_number! })); });
    supabase.rpc("get_public_libraries" as never)
      .then(({ data }) => setLibraries(
        ((data ?? []) as { id: string; name: string }[])
          .map((l) => ({ id: l.id, name: l.name }))
          .sort((a, b) => (a.name > b.name ? 1 : -1))
      ));
  }, [user]);

  const ALLOWED_COVER_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const MAX_COVER_BYTES = 10 * 1024 * 1024; // 10 MB after in-browser compression

  async function onCover(e: React.ChangeEvent<HTMLInputElement>) {
    const original = e.target.files?.[0]; if (!original) return;
    if (!ALLOWED_COVER_TYPES.includes(original.type)) {
      toast.error("Cover must be a JPEG, PNG, WEBP, or GIF image.");
      e.target.value = "";
      return;
    }
    // Compress in the browser so covers stay small no matter what the phone shot.
    let f = original;
    try { f = await compressImage(original); } catch { f = original; }
    if (f.size > MAX_COVER_BYTES) {
      toast.error("That image is too large even after compressing. Please pick a smaller one.");
      e.target.value = "";
      return;
    }
    if (f.size < original.size) {
      toast.success(`Cover optimised: ${formatBytes(original.size)} → ${formatBytes(f.size)}`);
    }
    setImportedCoverUrl(null);
    setCoverFile(f); setCoverPreview(URL.createObjectURL(f));
  }



  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSubmitting(true);
    try {
      let coords = getStoredLocation();
      if (!coords) {
        try { coords = await requestLocation(); }
        catch {
          toast.error("We need your location to put the book on the map. Please allow location access and try again.");
          setSubmitting(false);
          return;
        }
      }

      let cover_url: string | null = null;
      if (coverFile) {
        if (!ALLOWED_COVER_TYPES.includes(coverFile.type)) throw new Error("Invalid image type.");
        if (coverFile.size > MAX_COVER_BYTES) throw new Error("Image too large.");
        const ext = coverFile.name.split(".").pop() || "jpg";
        const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("book-covers").upload(path, coverFile, {
          upsert: false,
          contentType: coverFile.type,
        });
        if (upErr) throw upErr;
        cover_url = supabase.storage.from("book-covers").getPublicUrl(path).data.publicUrl;
      } else if (importedCoverUrl) {
        // Use the publisher-provided cover URL imported from the ISBN lookup.
        cover_url = importedCoverUrl;
      }


      // optionally save phone to profile
      if (form.phone.trim()) {
        await supabase.from("profiles").update({ phone_number: form.phone }).eq("id", user.id);
      }

      const { error } = await supabase.from("books").insert({
        user_id: user.id,
        title: form.title, author: form.author, isbn: form.isbn || null,
        cover_url,
        category: form.category, age_group: form.age_group,
        exchange_duration: form.exchange_mode === "lend" ? form.exchange_duration : "1 Month",
        condition: form.condition,
        exchange_mode: form.exchange_mode,
        description: form.description || null,
        lat: coords?.lat ?? null, lng: coords?.lng ?? null,
        is_donation: form.exchange_mode === "donate",
        library_id: form.library_id || null,
      });
      if (error) throw error;
      toast.success("Your book is now listed!");
      navigate({ to: "/my-books" });
    } catch (err: any) {
      console.error(err);
      toast.error("Could not list book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const valid =
    form.title && form.author && form.category && form.age_group && form.condition &&
    (form.exchange_mode !== "lend" || !!form.exchange_duration);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="text-3xl font-bold">List a book</h1>
        <p className="mt-1 text-sm text-muted-foreground">Share a book with neighbors. They'll borrow, read, and bring it back.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="rounded-xl border border-primary/30 bg-primary-soft/30 p-4">
            <Label htmlFor="isbn" className="flex items-center gap-1.5 text-base font-semibold">
              <Sparkles className="h-4 w-4 text-primary" />
              Have the book in hand? Auto-fill by ISBN
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Enter ISBN-10 or ISBN-13 and we'll pull the title, author, cover, and description for you. You can edit anything before listing.
            </p>
            <div className="mt-3 flex gap-2">
              <Input
                id="isbn"
                value={form.isbn}
                onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                placeholder="e.g. 9780132350884"
                inputMode="numeric"
              />
              <Button
                type="button"
                onClick={onIsbnLookup}
                disabled={isbnLookingUp || !form.isbn.trim()}
                className="shrink-0 bg-primary hover:bg-primary/90"
              >
                {isbnLookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : "Auto-fill"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Don't know the ISBN? Just skip and fill the fields manually below.
            </p>
          </div>

          <div>
            <Label>Cover image</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex h-32 w-24 items-center justify-center overflow-hidden rounded-lg bg-primary-soft">
                {coverPreview ? <img src={coverPreview} alt="" className="h-full w-full object-cover" /> : <Upload className="h-6 w-6 text-primary/40" />}
              </div>
              <Input type="file" accept="image/*" onChange={onCover} className="max-w-xs" />
            </div>
            {importedCoverUrl && !coverFile && (
              <p className="mt-1 text-xs text-muted-foreground">Cover imported from ISBN — upload your own photo to replace it.</p>
            )}
          </div>

          <div className="rounded-xl border border-primary/20 bg-primary-soft/30 p-4">
            <Label className="text-base font-semibold">How do you want to share it? *</Label>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {EXCHANGE_MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setForm({ ...form, exchange_mode: m.value })}
                  aria-pressed={form.exchange_mode === m.value}
                  className={`rounded-xl border p-3 text-start transition ${
                    form.exchange_mode === m.value
                      ? "border-primary bg-card shadow-[var(--shadow-card)]"
                      : "border-border bg-background hover:border-primary/50"
                  }`}
                >
                  <span className="block text-sm font-semibold">{m.emoji} {m.label}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{m.hint}</span>
                </button>
              ))}
            </div>
            {form.exchange_mode === "lend" && (
              <div className="mt-4">
                <FieldSelect
                  label="Lending duration *"
                  value={form.exchange_duration}
                  onChange={(v) => setForm({ ...form, exchange_duration: v })}
                  options={DURATIONS as any}
                />
                <p className="mt-1 text-xs text-muted-foreground">How long the borrower can keep it before returning it to you.</p>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" required maxLength={150} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="author">Author *</Label>
              <Input id="author" required maxLength={100} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
          </div>


          <div className="grid gap-4 md:grid-cols-2">
            <FieldSelect label="Category *" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES as any} />
            <FieldSelect label="Age group *" value={form.age_group} onChange={(v) => setForm({ ...form, age_group: v })} options={AGE_GROUPS as any} />
            <FieldSelect label="Condition *" value={form.condition} onChange={(v) => setForm({ ...form, condition: v })} options={CONDITIONS as any} />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" maxLength={500} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div>
            <Label htmlFor="phone">Contact number (optional)</Label>
            <Input id="phone" type="tel" placeholder={PHONE_PLACEHOLDER} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <p className="mt-1 text-xs text-muted-foreground">Optional. Readers can also message you in the app — no phone needed.</p>
          </div>


          {libraries.length > 0 && (
            <div>
              <Label>Drop-off at a community library (optional)</Label>
              <Select value={form.library_id || "none"} onValueChange={(v) => setForm({ ...form, library_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="No library" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No library</SelectItem>
                  {libraries.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button type="submit" disabled={!valid || submitting} className="w-full bg-primary hover:bg-primary/90">
            {submitting ? "Listing…" : "List my book"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
